import React, { useContext, useEffect, useState } from 'react';
import { searchTermChanged } from "../Redux/FiltersReducer";
import { showModal } from "../../../Redux/GlobalReducer";
import { MODAL_EDIT_BOOKING, TabContext } from "./BookingsLog";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationModal from "../../../Components/Modals/ConfirmationModal";
import { cancelBooking, deleteBooking } from "../Requests/BookingListRequests";

/**
 * Enables certain actions specified in the url to be performed
 * on the bookings log page.
 *
 * Some actions will need to search for a booking id to ensure the booking is
 * in the list before performing the desired action. This is done in two stages,
 * first a search filter will be applied, then the real action will be triggered
 * after the dataload count increases
 *
 * @todo - This kinda sucks - it's an elaborate solution to a problem that shouldn't exist
 * @todo    we can possibly do away with the need for this once
 * @todo    it's possible to edit/delete a booking separately from the booking list
 * @todo    ie after LPD-4491 is done
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const BookingsLogUrlActions = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {view, scope, categorySlug, actionType, actionId} = useParams();
    const tabState = useContext(TabContext);

    const filters = useSelector(state => state.module.BOOKINGS.filters);
    const bookings = useSelector(state => state.module.BOOKINGS.bookingList[view][scope].bookings);
    const dataLoadCount = useSelector(state => state.module.BOOKINGS.bookingList[view][scope].dataLoadCount);

    let [onLoadActionQueue, setOnLoadActionQueue] = useState([]);
    let [booking, setBooking] = useState(null);
    let [showCancelModal, setShowCancelModal] = useState(false);
    let [showDeleteModal, setShowDeleteModal] = useState(false);

    // Clear url action if present when user changes any filters
    useEffect(() => {
        // Only run the action after the two initial data loads have completed
        // (ie backwards and forwards load
        if (dataLoadCount > 1) {
            if (!filters.searchTerm && actionType) {
                clearUrlAction();
            }
        }
    }, [ filters ]);

    // If we have a special action in the url, set up queued tasks to be performed after data is loaded
    useEffect(() => {
        if (actionType && actionId) {
            queueUrlActions(actionType, actionId)
        }
    }, [actionType, actionId]);

    // Shonky queueing system so actions can be queued to happen after data is loaded
    useEffect(() => {
        if (dataLoadCount => 0) {
            nextQueuedAction();
        }
    }, [dataLoadCount])

    /**
     * Set up queued actions for various actions that might be requested in the url
     * @param actionType
     * @param actionId
     */
    const queueUrlActions = (actionType, actionId) => {
        switch(actionType) {
            case 'search':
                return queueActions([
                    ['search', [actionId]],
                ]);
            case 'edit':
                return queueActions([
                    ['search', [actionId]],
                    ['edit', [actionId]],
                ]);
            case 'delete':
                return queueActions([
                    ['search', [actionId]],
                    ['delete', [actionId]],
                ]);
        }
    }

    /**
     * Add actions to the onLoad queue
     * @param actions
     */
    const queueActions = (actions) => {
        let newActionQueue = [
            ...onLoadActionQueue,
            ...actions
        ]
        setOnLoadActionQueue(newActionQueue);
    }

    /**
     * Perform the next queued 'onLoad' action, if exists
     */
    const nextQueuedAction = () => {
        if (!onLoadActionQueue.length) { return; }

        let queued = onLoadActionQueue;
        let nextAction = queued.shift()

        setOnLoadActionQueue(queued);
        switch(nextAction[0]) {
            case 'search':
                return doSearchAction(...nextAction[1]);
            case 'edit':
                return doEditAction(...nextAction[1]);
            case 'delete':
                return doDeleteAction(...nextAction[1]);
        }
    }


    const doSearchAction = (searchTerm) => {
        dispatch( searchTermChanged(searchTerm) );
    }

    const doEditAction = (bookingId) => {
        let found = Object.values(bookings).find(booking => booking.slug === bookingId);
        if (found) {
            setBooking(found);
            dispatch( showModal(MODAL_EDIT_BOOKING, found.id) );
        }
    }

    const doDeleteAction = (bookingId) => {
        let found = Object.values(bookings).find(booking => booking.slug === bookingId);

        if (found) {
            setBooking(found);
            if (!!found.archived_at) {
                setShowDeleteModal(true)
            } else {
                setShowCancelModal(true);
            }
        }
    }

    const clearUrlAction = () => {
        navigate(`/bookings/logs/${view}/${scope}/${categorySlug}`);
    }

    return (<>
        <ConfirmationModal
            title="Cancel Booking"
            message="Are you sure you want to cancel this booking?"
            confirmText="Yes"
            cancelText="No"
            open={showCancelModal}
            onConfirm={(evt) => {
                dispatch( cancelBooking(booking.id, tabState.scope, tabState.view, 'single') )
                setShowCancelModal(false);
            }}
            onCancel={(evt) => setShowCancelModal(false)}
        />
        <ConfirmationModal
            title="Delete Booking"
            message="Are you sure you want to delete this booking?"
            confirmText="Yes"
            cancelText="No"
            open={showDeleteModal}
            onConfirm={(evt) => {
                dispatch( deleteBooking(booking.id, tabState.view, 'single') );
                setShowDeleteModal(false);
            }}
            onCancel={(evt) => setShowDeleteModal(false)}
        />
    </>);
};

export default BookingsLogUrlActions;
